import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';

export interface UserAlert {
  id: string;
  user_id: string;
  alert_type: 'low_credits' | 'delinquent' | 'restored' | 'credits_expired' | 'credits_expiring_soon' | 'free_trial_started' | 'appointment_scheduled' | 'appointment_rescheduled' | 'appointment_confirmed' | 'appointment_cancelled' | 'call_requires_attention' | 'other';
  message: string;
  status: 'unread' | 'read';
  created_at: string;
  read_at: string | null;
  call_log_id?: string | null;
}

// Hook to fetch all alerts for the current user
export function useUserAlerts() {
  return useQuery({
    queryKey: ['user-alerts'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('user_alerts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as UserAlert[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
}

// Hook to fetch only unread alerts
export function useUnreadAlerts() {
  return useQuery({
    queryKey: ['user-alerts-unread'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('user_alerts')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'unread')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as UserAlert[];
    },
    refetchInterval: 30000,
  });
}

// Hook to mark an alert as read
export function useMarkAlertRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from('user_alerts')
        .update({ 
          status: 'read', 
          read_at: new Date().toISOString() 
        })
        .eq('id', alertId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['user-alerts-unread'] });
    },
  });
}

// Hook to mark all alerts as read
export function useMarkAllAlertsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('user_alerts')
        .update({ 
          status: 'read', 
          read_at: new Date().toISOString() 
        })
        .eq('user_id', user.id)
        .eq('status', 'unread');
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['user-alerts-unread'] });
    },
  });
}

// Hook to dismiss (delete) an alert
export function useDismissAlert() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from('user_alerts')
        .delete()
        .eq('id', alertId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['user-alerts-unread'] });
    },
  });
} 